import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

const applicationId = 'a0fc56a5-d5fb-4335-912b-eed80735bc7a';
const clientToken = 'pub8c1e0f3f576449cfb7f5a64983ae09a6';
const site = 'datadoghq.com';
const service = 'dogbank-frontend';
const env = 'dogbank';
const version = '1.0.0';

const tracingUrls = [
  { match: /^http:\/\/localhost:8080\/api\//, propagatorTypes: ['tracecontext', 'datadog'] },
  { match: /^http:\/\/127\.0\.0\.1:8080\/api\//, propagatorTypes: ['tracecontext', 'datadog'] },
  { match: /\/api\//, propagatorTypes: ['tracecontext', 'datadog'] },
];

const redactSensitiveText = (value) => {
  if (typeof value !== 'string') return value;

  return value
    .replace(/\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/g, '***.***.***-**')
    .replace(/(["']?(?:password|senha)["']?\s*[:=]\s*["']?)[^"',}\s]+/gi, '$1[REDACTED]');
};

const sanitizeLogEvent = (event) => {
  event.message = redactSensitiveText(event.message);
  if (event.view?.url) {
    event.view.url = redactSensitiveText(event.view.url);
  }
  if (event.http?.url) {
    event.http.url = redactSensitiveText(event.http.url);
  }
  return true;
};

const globalScope = typeof window !== 'undefined' ? window : {};

if (!globalScope.__DOGBANK_FRONTEND_DATADOG_INITIALIZED__) {
  datadogLogs.init({
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: 100,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['log', 'info', 'warn', 'error'],
    beforeSend: sanitizeLogEvent,
  });

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    defaultPrivacyLevel: 'allow',
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: tracingUrls,
    traceSampleRate: 100,
  });

  datadogRum.startSessionReplayRecording();
  datadogLogs.logger.info('dogbank.frontend.logs.initialized', {
    route: globalScope.location?.pathname,
    source: 'react',
  });

  globalScope.__DOGBANK_FRONTEND_DATADOG_INITIALIZED__ = true;
}

// Deriva um e-mail de demonstração determinístico a partir do nome. O backend de
// login não expõe e-mail; isto apenas preenche user.email no RUM/Logs sem tocar
// no backend ou no banco (ver PLANO-MELHORIAS-DATADOG.md).
const toDemoEmail = (nome) => {
  if (!nome || typeof nome !== 'string') return undefined;
  const slug = nome
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // remove acentos
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');
  return slug ? `${slug}@dogbank.demo` : undefined;
};

// Associa o usuário autenticado à sessão RUM e aos Logs (user.id/name/email).
// Usa accountId como id (evita expor CPF no RUM); tolerante a falhas para nunca
// interferir no fluxo de login.
const setDatadogUser = (user) => {
  if (!user) return;
  try {
    const id = user.accountId != null ? String(user.accountId) : user.cpf;
    const payload = {
      id,
      name: user.nome,
      email: toDemoEmail(user.nome),
    };
    datadogRum.setUser(payload);
    datadogLogs.setUser?.(payload);
  } catch (e) {
    // Observabilidade nunca deve quebrar a aplicação.
    console.warn('⚠️ Failed to set user in Datadog:', e);
  }
};

// Limpa a associação de usuário (logout).
const clearDatadogUser = () => {
  try {
    datadogRum.clearUser();
    datadogLogs.clearUser?.();
  } catch (e) {
    console.warn('⚠️ Failed to clear user in Datadog:', e);
  }
};

export { datadogLogs, datadogRum, setDatadogUser, clearDatadogUser };
